import { useCallback, useMemo } from "react";

import { useFlightStaticData } from "../../../../core/flight-static-data";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";

import { useLocalizedDateFormat } from "../../../../core";
import { FlightSearchAPIData } from "../../FlightSearch/types";
import {
  FlightPrebookStorageDataLegacy,
  FlightSummaryBaseComponentProps,
} from "../FlightSummary";
import {
  getSummaryFromApiSearchData,
  getSummaryFromPrebookStorage,
  getTripFromApiSearchData,
  getTripFromPrebookStorage,
} from "../FlightSummary/utils";

export function useTransformFlightApiSearchDataIntoSummaryProps(
  data: FlightSearchAPIData[]
): FlightSummaryBaseComponentProps[] {
  const { format: formatter } = useLocalizedDateFormat();
  const { isError, isLoading, data: staticData } = useFlightStaticData();
  const { CorporateEnumSeatClass, CorporateFlightGeneral } =
    useMobileContentResource();

  const createSummary = useCallback(
    (type: "DEPART" | "RETURN", journey: FlightSearchAPIData["journeys"]) => {
      const combineContentResource = {
        ...CorporateEnumSeatClass,
        ...CorporateFlightGeneral,
      };
      return {
        type,
        summary: getSummaryFromApiSearchData(
          journey,
          staticData,
          combineContentResource,
          formatter
        ),
        trip: getTripFromApiSearchData(
          journey,
          staticData,
          combineContentResource,
          formatter
        ),
        isCompliant: true, // Default to true as API does not provide this info
      };
    },
    [staticData, CorporateEnumSeatClass, CorporateFlightGeneral, formatter]
  );

  return useMemo(() => {
    if (isError || isLoading) return [];

    const [departJourney, returnJourney] = data.map((item) => item.journeys);
    const summaries: FlightSummaryBaseComponentProps[] = [
      createSummary("DEPART", departJourney),
    ];
    if (returnJourney) summaries.push(createSummary("RETURN", returnJourney));

    return summaries;
  }, [isError, isLoading, data, createSummary]);
}

export function useTransformFlightPrebookStorageIntoSummaryProps() {
  const { format: formatter } = useLocalizedDateFormat();
  const { data: staticData } = useFlightStaticData();
  const { CorporateFlightGeneral: cr } = useMobileContentResource();

  const createSummary = useCallback(
    (type: "DEPART" | "RETURN", trip: FlightPrebookStorageDataLegacy) => {
      const { journeys, summary } = trip;
      return {
        type,
        summary: getSummaryFromPrebookStorage(
          summary,
          journeys,
          staticData,
          cr,
          formatter
        ),
        trip: getTripFromPrebookStorage(journeys, staticData, cr, formatter),
        isCompliant: !trip.compliance,
      };
    },
    [staticData, cr, formatter]
  );

  return useCallback(
    (
      data: FlightPrebookStorageDataLegacy[]
    ): FlightSummaryBaseComponentProps[] => {
      const [departTrip, returnTrip] = data;
      const summaries: FlightSummaryBaseComponentProps[] = [
        createSummary("DEPART", departTrip),
      ];
      if (returnTrip) summaries.push(createSummary("RETURN", returnTrip));
      return summaries;
    },
    [createSummary]
  );
}
